import { useState } from "react";
import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import BeatPulse from "../components/BeatPulse";

/**
 * Scene 18 — Demo of the video system. Static screenshot + descriptor copy.
 * Drop /public/video-tool.png to replace the screenshot.
 */

const VIDEO_TOOL_SRC = "/video-tool.png";
const GREEN = "#96C11F";

export default function Scene18_VideoSystemDemo() {
  const [imageOk, setImageOk] = useState(true);
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-video-system-demo">
      <div className="relative max-w-[1500px] mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-4">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-3 kn-eyebrow"
          >
            <BeatPulse size={9} label="Live demo" />
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="kn-display text-4xl sm:text-5xl lg:text-6xl text-white mt-8"
            data-testid="video-demo-title"
          >
            The video system.
            <br />
            <span style={{ color: GREEN }}>Watch it run.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-3"
          >
            {["Reels", "Stories", "Shorts", "Long-form"].map((tag, i) => (
              <motion.span
                key={tag}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4 + i * 0.12, duration: 0.6 }}
                className="px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-[0.12em]"
                style={{
                  background: "rgba(150,193,31,0.14)",
                  border: "1px solid rgba(150,193,31,0.45)",
                  color: GREEN,
                }}
              >
                {tag}
              </motion.span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-8 relative">
          <motion.figure
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-2xl overflow-hidden"
            style={{
              aspectRatio: "16 / 9",
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${GREEN}33`,
              boxShadow: `0 30px 80px -20px rgba(0,0,0,0.7), 0 0 60px ${GREEN}1f`,
            }}
            data-testid="video-system-frame"
          >
            {imageOk ? (
              <img
                src={VIDEO_TOOL_SRC}
                alt="NicheCom video tool screenshot"
                className="absolute inset-0 w-full h-full object-contain"
                onError={() => setImageOk(false)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  className="text-[11px] tracking-[0.32em] uppercase"
                  style={{ color: "rgba(255,255,255,0.45)" }}
                >
                  Drop screenshot at /public/video-tool.png
                </div>
              </div>
            )}
          </motion.figure>
        </div>
      </div>
    </SceneShell>
  );
}
